import { Component, lazy, Suspense, useRef, useState, type ComponentType, type ErrorInfo, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { focusLater } from '../focus';
import { freshLoader } from '../lazyRetry';
import { useTrayDock } from '../trayDock';
import { Icon } from './Icon';
import { dropToast, toast } from './Toast';

/**
 * 담은 원단 트레이 자리 (12회차 Codex 4차 N15).
 *
 * 트레이는 지연 청크다. 청크를 못 받으면(네트워크 끊김 · 배포 직후 옛 청크 이름) React.lazy 가 오류를 던지는데,
 * 트레이는 본문 오류 경계 밖이라 앱 전체가 흰 화면이 됐다. 트레이만 감싸는 오류 경계를 두고:
 *  · 트레이 버튼 자리에 「담은 원단 n · 다시 시도」 버튼(같은 자리 · 같은 크기 — 휴대폰 사진 검색에서는 실행 막대 왼쪽 칸)
 *  · 한 번 알림(토스트) — 담은 원단은 이 브라우저에 그대로 있다는 것까지
 *  · 누르면 청크를 **새로 받는다**(새 lazy — React.lazy 는 실패한 약속을 기억해 같은 것으로는 다시 받지 않는다)
 * 검색 · 내비 · 본문은 그대로 쓸 수 있다.
 */
type Loader = () => Promise<{ default: ComponentType }>;

/** 다시 받기 — 먼저 같은 주소로, 안 되면 주소 끝에 시도 번호를 붙여 새로 받는다(lazyRetry.ts 의 freshLoader — 화면 청크와 같은 방식, 14회차 m63) */
const fresh = (load: Loader): Loader => freshLoader(load);

interface BProps { count: number; onRetry: () => void; onFail: (toastId: number) => void; dock: HTMLElement | null; children: ReactNode }
interface BState { failed: boolean }

class TrayBoundary extends Component<BProps, BState> {
  state: BState = { failed: false };

  static getDerivedStateFromError(): BState {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[담은 원단 트레이]', error, info.componentStack);
    this.props.onFail(toast('담은 원단 목록을 불러오지 못했습니다 — 「담은 원단 · 다시 시도」 버튼을 누르세요. 담은 원단은 그대로 있습니다', 'error'));
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    const { count: n, dock } = this.props;
    // 트레이 버튼과 같은 자리 — 사진 검색에서는 실행 줄 왼쪽 자리(m58)
    const btn = (
      <button type="button" className={`tray-fab tray-fab-err${dock ? ' docked' : ''}`} onClick={this.props.onRetry}
              aria-label={`담은 원단 ${n}개 — 목록을 불러오지 못했습니다. 다시 시도`} title="담은 원단 목록을 다시 불러옵니다">
        <Icon name="refresh" size={18} />
        <span><span className="tray-fab-text">담은 원단 </span><b>{n}</b><span className="tray-fab-text"> · 다시 시도</span></span>
      </button>
    );
    return dock ? createPortal(btn, dock) : btn;
  }
}

export default function TraySlot({ load, count }: { load: Loader; count: number }) {
  // 시도마다 새 lazy — 다시 시도하면 청크를 새로 요청한다
  const [slot, setSlot] = useState(() => ({ n: 0, Comp: lazy(load) }));
  /** 실패 알림 — 다시 받기가 성공하면 내린다(13회차 관찰 ④: 정상 버튼과 「불러오지 못했습니다」 가 2초 넘게 함께 보였다) */
  const failToast = useRef(0);
  const retry = () => {
    const again = fresh(load);
    setSlot((s) => ({ n: s.n + 1, Comp: lazy(() => again().then((m) => { if (failToast.current) { dropToast(failToast.current); failToast.current = 0; } return m; })) }));
    // 누른 버튼은 받는 동안 사라진다 — 트레이 버튼(또는 다시 실패한 버튼)이 나오면 그리로(그사이 다른 곳을 고르면 옮기지 않는다)
    focusLater(() => document.querySelector<HTMLElement>('.tray-fab'), () => null, { tries: 250 });
  };
  const { n, Comp } = slot;
  const dock = useTrayDock();
  return (
    <TrayBoundary key={n} count={count} onRetry={retry} onFail={(id) => { failToast.current = id; }} dock={dock}>
      <Suspense fallback={null}><Comp /></Suspense>
    </TrayBoundary>
  );
}
